import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Grid, Typography, Autocomplete, TextField, Box } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Loader } from '../../../components'
import {
  getFormattedStartDateString,
  getFormattedEndDateString,
  setBillFormat,
  getBillFormat,
} from '../../../utils'
import { fetchBillFormats } from '../../../services'
import { previewBulkInvoices } from './Services'
import { BulkInvoicePreviewItem } from './types'

export type PreviewResult =
  | { ok: true; items: BulkInvoicePreviewItem[]; billFormat: string }
  | { ok: false; error: string }

export interface InvoicesDatesStepHandle {
  runPreview: () => Promise<PreviewResult>
}

export interface InvoicesDatesStepProps {
  selectedSiteIds: number[]
  externalError?: string | null
  loading?: boolean
}

export const InvoicesDatesStep = forwardRef<
  InvoicesDatesStepHandle,
  InvoicesDatesStepProps
>(function InvoicesDatesStep(
  { selectedSiteIds, externalError, loading: externalLoading },
  ref
) {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [format, setFormat] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [internalLoading, setInternalLoading] = useState(false)
  const [formatOptions, setFormatOptions] = useState<string[]>([])
  const [formatsLoading, setFormatsLoading] = useState(false)

  const loading = externalLoading || internalLoading
  const [datesInitialized, setDatesInitialized] = useState(false)

  useEffect(() => {
    if (datesInitialized) return

    const today = new Date()
    setInvoiceDate(today)
    const currentDay = today.getDate()
    let prefillStartDate: Date
    let prefillEndDate: Date

    if (currentDay > 20) {
      prefillStartDate = new Date(today.getFullYear(), today.getMonth(), 1)
      prefillEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    } else {
      prefillStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      prefillEndDate = new Date(today.getFullYear(), today.getMonth(), 0)
    }

    setStartDate(prefillStartDate)
    setEndDate(prefillEndDate)
    setDatesInitialized(true)
  }, [datesInitialized])

  useEffect(() => {
    const siteId = selectedSiteIds.length > 0 ? selectedSiteIds[0] : 0
    let cancelled = false
    setFormatsLoading(true)
    fetchBillFormats(siteId)
      .then((res) => {
        if (cancelled) return
        if (!res.success || !Array.isArray(res.data)) {
          setFormatOptions([])
          setFormat(null)
          return
        }
        const list = [...res.data].sort()
        setFormatOptions(list)
        const session = getBillFormat()
        setFormat(session && list.includes(session) ? session : list[0] ?? null)
      })
      .catch(() => {
        if (!cancelled) {
          setFormatOptions([])
          setFormat(null)
        }
      })
      .finally(() => {
        if (!cancelled) setFormatsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedSiteIds])

  useImperativeHandle(ref, () => ({
    runPreview: async (): Promise<PreviewResult> => {
      if (!startDate || !endDate || !invoiceDate || !format) {
        const msg =
          'Start date, end date, invoice date, and bill format are required.'
        setError(msg)
        return { ok: false, error: msg }
      }
      if (formatsLoading) {
        return { ok: false, error: 'Bill formats are still loading.' }
      }

      const startDateString = getFormattedStartDateString(new Date(startDate), false)
      const endDateString = getFormattedEndDateString(new Date(endDate), false)
      const invoiceDateString = getFormattedEndDateString(new Date(invoiceDate), false)

      setInternalLoading(true)
      setError(null)
      try {
        setBillFormat(format)
        const response = await previewBulkInvoices(
          startDateString,
          endDateString,
          invoiceDateString,
          format,
          selectedSiteIds
        )

        if (!response?.success) {
          const msg = response?.message || 'Could not load invoice preview.'
          setError(msg)
          return { ok: false, error: msg }
        }

        const items = response.data ?? []
        if (items.length === 0) {
          const msg = 'No invoices to create for the selected period.'
          setError(msg)
          return { ok: false, error: msg }
        }

        return { ok: true, items, billFormat: format }
      } catch {
        const msg = 'Error occurred while loading invoice preview.'
        setError(msg)
        return { ok: false, error: msg }
      } finally {
        setInternalLoading(false)
      }
    },
  }))

  const displayError = externalError || error

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        py: 2,
        position: 'relative',
      }}
    >
      {loading && <Loader />}
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1">
              {`Creating invoices for ${selectedSiteIds.length} selected site(s). Please select start and end date to create invoices.`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label="Invoice Date"
              value={invoiceDate}
              onChange={(value) => {
                setInvoiceDate(value)
                setError(null)
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(value) => {
                setStartDate(value)
                setError(null)
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(value) => {
                setEndDate(value)
                setError(null)
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              options={formatOptions}
              loading={formatsLoading}
              disabled={formatsLoading}
              getOptionLabel={(option) => option}
              value={format}
              onChange={(_event, value) => {
                setFormat(value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bill Format"
                  fullWidth
                  helperText={formatsLoading ? 'Loading formats…' : null}
                />
              )}
            />
           
          </Grid>
          {displayError && (
            <Grid item xs={12}>
              <Typography color="error">{displayError}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  )
})

export default InvoicesDatesStep
